/**
 *  @swagger
 *  definitions:
 *    CrewmemberRegattaNotificationParams:
 *      type: object
 *      properties:
 *        CrewmemberId:
 *          description: ID of the associated Crewmember
 *          type: string
 *        RegattaNotificationId:
 *          description: ID of the associated RegattaNotification
 *          type: string
 *        Sent:
 *          description: Sent of the RegattaNotification
 *          type: string
 *          format: date-time
 *        Read:
 *          description: Read of the RegattaNotification
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    CrewmemberRegattaNotificationCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberRegattaNotificationParams'
 *        - type: object
 *          required:
 *            - CrewmemberId
 *            - RegattaNotificationId
 *
 *  @swagger
 *  definitions:
 *    CrewmemberRegattaNotificationQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the CrewmemberRegattaNotification
 *              type: string
 *        - $ref: '#/definitions/CrewmemberRegattaNotificationParams'
 *
 *  @swagger
 *  definitions:
 *    CrewmemberRegattaNotificationUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberRegattaNotificationQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    CrewmemberRegattaNotification:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberRegattaNotificationCreateParams'
 *        - $ref: '#/definitions/CrewmemberRegattaNotificationUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      CrewmemberRegattaNotification:
 *        allOf:
 *          - $ref: '#/definitions/CrewmemberRegattaNotification'
 *          - type: object
 *            properties:
 *              Crewmember:
 *                description: Crewmember of the CrewmemberRegattaNotification
 *                $ref: '#/components/schemas/Crewmember'
 *              RegattaNotification:
 *                description: RegattaNotification of the CrewmemberRegattaNotification
 *                $ref: '#/components/schemas/RegattaNotification'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  CrewmemberId: string;
  RegattaNotificationId: string;
};

export type $Params = {
  Read?: Date | null;
  Sent?: Date | null;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Crewmember?: $Model<'Crewmember'>;
  RegattaNotification?: $Model<'RegattaNotification'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Read: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Sent: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'CrewmemberRegattaNotification'> => {
  const CrewmemberRegattaNotification = stack.define(
    'CrewmemberRegattaNotification',
    fields(Sequelize),
    {
      tableName: 'CrewmemberRegattaNotifications',
    },
  );

  CrewmemberRegattaNotification.associate = (models: $ModelClasses) => {
    models.CrewmemberRegattaNotification.belongsTo(
      models.Crewmember,
      {
        as: 'Crewmember',
        foreignKey: 'CrewmemberId',
      },
    );
    models.CrewmemberRegattaNotification.belongsTo(
      models.RegattaNotification,
      {
        as: 'RegattaNotification',
        foreignKey: 'RegattaNotificationId',
      },
    );
  };

  CrewmemberRegattaNotification.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return CrewmemberRegattaNotification;
});
